import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { PageFormShell } from "../ui/PageFormShell";
import { DocHeader } from "../ui/DocHeader";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { TesisPickerField, useTesisPicker, resolveTesisId } from "../ui/TesisPickerField";
import { trpc } from "../../lib/trpc";
import "./ItemFormModal.css";

export type ItemKind = "stock" | "semi" | "finished";

/**
 * Grup kartı (.fgroup) — ShipmentDetailDrawer / TransferDetailDrawer deseni.
 * hint → .fgroup-head .s (durum özeti ya da kod rozeti); `mono` mono rozet yapar.
 * note → kartın DİBİNE yapışık bilgi şeridi (≤110 karakter, bkz. .css TUZAK 4).
 * .fgroup-head .s nowrap olduğu için `hint` verildiğinde `title` ZORUNLU.
 */
function Grp({ icon, title, hint, hintTitle, mono, core, note, children }: {
  icon: string; title: string; hint?: string; hintTitle?: string; mono?: boolean;
  core?: boolean; note?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className={`fgroup${core ? " itf-core" : ""}`}>
      <div className="fgroup-head">
        <span className="ic"><Icon name={icon} size={13} /></span>
        <span className="t">{title}</span>
        {hint ? <span className={`s${mono ? " itf-badge" : ""}`} title={hintTitle ?? hint}>{hint}</span> : null}
      </div>
      <div className="fgroup-body">{children}</div>
      {note ? (
        <div className="fgroup-foot itf-note">
          <span className="ic"><Icon name="info" size={13} /></span>
          {/* TEK sarmalayıcı — .fgroup-foot flex kabı, cümleler yoksa yatay dağılır. */}
          <div>{note}</div>
        </div>
      ) : null}
    </section>
  );
}

/** Katlanır grup kartı — opsiyonel bloklar. `open` KONTROLLÜ prop (zorunlu). */
function Fold({ icon, title, hint, note, open, onToggle, children }: {
  icon: string; title: string; hint?: string; note?: React.ReactNode;
  open: boolean; onToggle: (v: boolean) => void; children: React.ReactNode;
}) {
  return (
    <details
      className="fgroup itf-fold"
      open={open}
      onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="fgroup-head">
        <span className="ic"><Icon name={icon} size={13} /></span>
        <span className="t">{title}</span>
        {hint ? <span className="s" title={hint}>{hint}</span> : null}
        <span className="itf-chev" aria-hidden="true"><Icon name="chevDown" size={14} /></span>
      </summary>
      <div className="fgroup-body">{children}</div>
      {note ? (
        <div className="fgroup-foot itf-note">
          <span className="ic"><Icon name="info" size={13} /></span>
          <div>{note}</div>
        </div>
      ) : null}
    </details>
  );
}

interface ItemFormModalProps {
  open: boolean;
  kind: ItemKind;
  /** Edit modu: liste satırı (id, code, name...). undefined ise create modu. */
  initial?: ExistingItem | null;
  onClose: () => void;
  /** Create modunda başarıyla oluşturulan kartın id'si — çağıran (ör. Mal Kabul) hemen seçebilsin. */
  onCreated?: (item: { id: string; kind: ItemKind }) => void;
  /** Create modunda ön-seçili Kart Türü (ör. Mal Kabul'de seçili türle eşleşsin → listede görünsün). */
  presetItemTypeId?: string;
  /**
   * "drawer" (varsayılan) = çekmece · "page" = uygulama içi SEKME olarak tam sayfa.
   * Sarmalayıcı dışında hiçbir şey değişmez; formun gövdesi ikisinde de AYNI koddur.
   */
  variant?: "drawer" | "page";
}

interface ExistingItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  baseUomId: string;
  shelfLifeDays: number | null;
  allergens: string[];
  isActive: boolean;
  reorderPoint?: number | string | null;
  leadTimeDays?: number | null;
  defaultSupplierId?: string | null;
  qcRequired?: boolean;
  consumptionMethod?: "backflush" | "manual";
}

interface FormState {
  code: string;
  name: string;
  description: string;
  baseUomId: string;
  shelfLifeDays: string;
  allergens: string[];
  reorderPoint: string;
  leadTimeDays: string;
  defaultSupplierId: string;
  qcRequired: boolean;
  consumptionMethod: "backflush" | "manual";
  recipeId: string; // bağlı reçete (yarı mamul/mamul) — ortak kütüphane
  // Esnek alanlar (Ayarlar)
  itemTypeId: string;
  altUomId: string;
  altUomFactor: string;
  unitsPerPack: string;
  packsPerBox: string;
  boxesPerPallet: string;
  customFields: Record<string, string>;
}

const EMPTY: FormState = {
  code: "", name: "", description: "", baseUomId: "", shelfLifeDays: "",
  allergens: [], reorderPoint: "", leadTimeDays: "", defaultSupplierId: "", qcRequired: false, consumptionMethod: "backflush", recipeId: "",
  itemTypeId: "", altUomId: "", altUomFactor: "", unitsPerPack: "", packsPerBox: "", boxesPerPallet: "",
  customFields: {},
};

const RECIPE_STATUS: Record<string, string> = { draft: "taslak", active: "aktif", archived: "arşiv" };

const KIND_TITLE: Record<ItemKind, { create: string; edit: string }> = {
  stock: { create: "Yeni Stok Kartı", edit: "Stok Kartı Düzenle" },
  semi: { create: "Yeni Yarı Mamul", edit: "Yarı Mamul Düzenle" },
  finished: { create: "Yeni Mamul", edit: "Mamul Düzenle" },
};

/** Belge künyesinde yazan tür adı ("Yeni"/"Düzenle" fiili olmadan). */
const KIND_LABEL: Record<ItemKind, string> = {
  stock: "Stok Kartı",
  semi: "Yarı Mamul",
  finished: "Mamul",
};

/**
 * Birim boyutu (`Uom.dimension`) Türkçe etiketi — seçim listesinde parantez içinde.
 * DB'de İngilizce saklanır (weight/volume/count/length); Türkçe arayüzde ham değeri
 * göstermek CLAUDE.md ✅#4'e aykırı. Bilinmeyen boyut ham haliyle geçer (yeni bir
 * boyut eklendiğinde etiket unutulsa bile seçenek KAYBOLMAZ, sadece çevrilmez).
 */
const UOM_DIM_LABEL: Record<string, string> = {
  weight: "ağırlık",
  volume: "hacim",
  count: "sayı",
  length: "uzunluk",
  area: "alan",
};

const s = (v: unknown): string => (v == null ? "" : String(v));

/** Liste satırı (initial) ya da detay (get) objesinden form state üret. */
function buildFormState(src: Record<string, unknown> | null | undefined): FormState {
  if (!src) return EMPTY;
  const cf: Record<string, string> = {};
  const cfArr = (src.customFields as Array<{ defId: string; value: string }> | undefined) ?? [];
  for (const c of cfArr) cf[c.defId] = c.value;
  return {
    code: s(src.code),
    name: s(src.name),
    description: s(src.description),
    baseUomId: s(src.baseUomId),
    shelfLifeDays: s(src.shelfLifeDays),
    allergens: (src.allergens as string[] | undefined) ?? [],
    reorderPoint: s(src.reorderPoint),
    leadTimeDays: s(src.leadTimeDays),
    defaultSupplierId: s(src.defaultSupplierId),
    qcRequired: (src.qcRequired as boolean | undefined) ?? false,
    consumptionMethod: (src.consumptionMethod as "backflush" | "manual" | undefined) ?? "backflush",
    recipeId: s(src.recipeId),
    itemTypeId: s(src.itemTypeId),
    altUomId: s(src.altUomId),
    altUomFactor: s(src.altUomFactor),
    unitsPerPack: s(src.unitsPerPack),
    packsPerBox: s(src.packsPerBox),
    boxesPerPallet: s(src.boxesPerPallet),
    customFields: cf,
  };
}

export function ItemFormModal({ open, kind, initial, onClose, onCreated, presetItemTypeId, variant = "drawer" }: ItemFormModalProps) {
  const isEdit = !!initial;
  const [state, setState] = useState<FormState>(EMPTY);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const qc = useQueryClient();
  // Ana veri tesis seçimi (yalnız süper-admin "Tüm Tesisler" + create).
  const tp = useTesisPicker(!isEdit);
  const [tesisPick, setTesisPick] = useState("");

  const uomQ = trpc.uom.list.useQuery(undefined, { staleTime: 5 * 60_000 });
  const allergensQ = trpc.settings.allergen.list.useQuery(undefined, { enabled: open });
  const typesQ = trpc.settings.itemType.list.useQuery({ kind }, { enabled: open });
  const customDefsQ = trpc.settings.customField.list.useQuery({ appliesTo: kind }, { enabled: open });
  // Ortak reçete kütüphanesi — yarı mamul/mamul'e bağlanabilir hazır reçeteler.
  const recipesQ = trpc.recipe.list.useQuery({ page: 1, pageSize: 100 }, { enabled: open && kind !== "stock" });
  // Varsayılan tedarikçi seçici (yalnız ham madde) — MRP/satın alma talebi ön-dolgusu.
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { enabled: open && kind === "stock", staleTime: 60_000 });
  const supplierOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((sp) => ({ value: sp.id, label: sp.name, tail: sp.code })), [suppliersQ.data]);

  // Edit modunda tam detay (paketleme, özel alanlar vb.) get'ten gelir.
  const stockDetailQ = trpc.stockCard.get.useQuery({ id: initial?.id ?? "" }, { enabled: open && isEdit && kind === "stock" });
  const semiDetailQ = trpc.semiProduct.get.useQuery({ id: initial?.id ?? "" }, { enabled: open && isEdit && kind === "semi" });
  const finishedDetailQ = trpc.finishedProduct.get.useQuery({ id: initial?.id ?? "" }, { enabled: open && isEdit && kind === "finished" });
  const detailQ = kind === "stock" ? stockDetailQ : kind === "semi" ? semiDetailQ : finishedDetailQ;
  const detail = detailQ.data;
  /**
   * DÜZENLEMEDE DETAY GELMEDEN KAYDETMEK VERİ SİLİYORDU.
   *
   * Form önce liste satırından (`initial`) doluyor; o satırda itemTypeId, altUomId,
   * altUomFactor, paketleme üçlüsü, defaultSupplierId ve recipeId YOK. Bu alanlar
   * yalnız get() cevabıyla geliyor. Arada "Güncelle"ye basılırsa buildExtras() boş
   * string'leri null'a çevirip gönderiyor ve kartın tür/paketleme/tedarikçi/REÇETE
   * bağı sessizce siliniyordu (ekranda hata yok, audit'e normal update yazılıyordu).
   */
  const detailReady = !isEdit || !!detail;

  // Form'u doldur: create → EMPTY; edit → detay (varsa) yoksa liste satırı.
  useEffect(() => {
    if (!open) return;
    if (!isEdit) { setState({ ...EMPTY, itemTypeId: presetItemTypeId ?? "" }); setSubmitErr(null); setConfirmOpen(false); setTesisPick(""); return; }
    setState(buildFormState((detail as Record<string, unknown>) ?? (initial as unknown as Record<string, unknown>)));
    setSubmitErr(null);
    setConfirmOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, detail]);

  const createStock = trpc.stockCard.create.useMutation();
  const updateStock = trpc.stockCard.update.useMutation();
  const createSemi = trpc.semiProduct.create.useMutation();
  const updateSemi = trpc.semiProduct.update.useMutation();
  const createFinished = trpc.finishedProduct.create.useMutation();
  const updateFinished = trpc.finishedProduct.update.useMutation();

  // Otomatik kod önizlemesi (create modu): isim + Kart Türü → sunucu kodu üretir (debounce'lı).
  // Önek kuralı YALNIZ sunucuda (mamulde MM-, diğerlerinde tür kodu) — istemci kopyalamaz, önizlemeyi gösterir.
  const [debouncedName, setDebouncedName] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(state.name.trim()), 300);
    return () => clearTimeout(t);
  }, [state.name]);
  const codePreviewQ = trpc.itemCode.preview.useQuery(
    { kind, itemTypeId: state.itemTypeId || null, name: debouncedName },
    { enabled: open && !isEdit && debouncedName.length > 0, staleTime: 3_000 },
  );
  const previewCode = isEdit ? state.code : (codePreviewQ.data?.code ?? "");

  const toggleStock = trpc.stockCard.toggleActive.useMutation();
  const toggleSemi = trpc.semiProduct.toggleActive.useMutation();
  const toggleFinished = trpc.finishedProduct.toggleActive.useMutation();
  const toggling = toggleStock.isPending || toggleSemi.isPending || toggleFinished.isPending;

  async function doToggleActive() {
    if (!initial) return;
    const payload = { id: initial.id, isActive: !initial.isActive };
    try {
      if (kind === "stock") await toggleStock.mutateAsync(payload);
      else if (kind === "semi") await toggleSemi.mutateAsync(payload);
      else await toggleFinished.mutateAsync(payload);
      await qc.invalidateQueries();
      setConfirmOpen(false);
      onClose();
    } catch { /* banner */ }
  }

  const isPending =
    createStock.isPending || updateStock.isPending ||
    createSemi.isPending || updateSemi.isPending ||
    createFinished.isPending || updateFinished.isPending;

  const errorMessage = useMemo(() => {
    const mut =
      createStock.error ?? updateStock.error ?? createSemi.error ?? updateSemi.error ?? createFinished.error ?? updateFinished.error;
    if (submitErr) return submitErr;
    if (detailQ.error) return `Kart detayı yüklenemedi: ${detailQ.error.message}`;
    if (!mut) return null;
    const zod = (mut.data as { zodError?: { fieldErrors?: Record<string, string[]> } } | undefined)?.zodError?.fieldErrors;
    if (zod) {
      const msgs = Object.values(zod).flat().filter(Boolean) as string[];
      if (msgs.length) return msgs.join(" · ");
    }
    return mut.message ?? "Bilinmeyen hata";
  }, [submitErr, detailQ.error, createStock.error, updateStock.error, createSemi.error, updateSemi.error, createFinished.error, updateFinished.error]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((st) => ({ ...st, [key]: value }));
  }
  function toggleAllergen(name: string) {
    setState((st) => ({
      ...st,
      allergens: st.allergens.includes(name) ? st.allergens.filter((a) => a !== name) : [...st.allergens, name],
    }));
  }
  function setCustomField(defId: string, value: string) {
    setState((st) => ({ ...st, customFields: { ...st.customFields, [defId]: value } }));
  }

  /** Esnek alan payload'u — create + update'in ikisinde de aynı. */
  function buildExtras() {
    const num = (v: string) => (v.trim() ? Number(v) : null);
    return {
      itemTypeId: state.itemTypeId || null,
      altUomId: state.altUomId || null,
      altUomFactor: num(state.altUomFactor),
      unitsPerPack: num(state.unitsPerPack),
      packsPerBox: num(state.packsPerBox),
      boxesPerPallet: num(state.boxesPerPallet),
      allergens: state.allergens,
      customFields: Object.entries(state.customFields)
        .map(([defId, value]) => ({ defId, value }))
        .filter((c) => c.value.trim() !== "" || true), // boşlar da gönderilir → temizlenir
    };
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitErr(null);
    if (isEdit && !detailReady) return setSubmitErr("Kart detayı henüz yüklenmedi — birkaç saniye sonra tekrar deneyin.");
    if (!state.name.trim()) return setSubmitErr("Ad zorunlu.");
    if (!state.baseUomId) return setSubmitErr("Birim zorunlu.");
    if (tp.show && !tesisPick) return setSubmitErr("Tesis seçin (Global dahil).");
    // Zorunlu özel alanlar: yıldız gösteriliyordu ama ne istemci ne sunucu
    // doğruluyordu (item-extras.ts `required` bayrağını hiç okumuyor) → boş
    // bırakılan "zorunlu" izlenebilirlik alanı sessizce kaydediliyordu.
    const eksikOzel = customDefs.filter((d) => d.required && (state.customFields[d.id] ?? "").trim() === "");
    if (eksikOzel.length) return setSubmitErr(`Zorunlu özel alan boş: ${eksikOzel.map((d) => d.label).join(", ")}`);

    const extras = buildExtras();
    const common = {
      name: state.name,
      baseUomId: state.baseUomId,
      ...extras,
    };
    const createCommon = {
      // code gönderilmez → sunucu kategori-isim-sıra ile otomatik üretir.
      ...common,
      description: state.description || undefined,
      shelfLifeDays: state.shelfLifeDays ? Number(state.shelfLifeDays) : undefined,
      tesisId: resolveTesisId(tp.show, tesisPick),
    };
    const updateCommon = {
      id: initial?.id ?? "",
      ...common,
      description: state.description || null,
      shelfLifeDays: state.shelfLifeDays ? Number(state.shelfLifeDays) : null,
    };

    let createdId: string | null = null;
    try {
      if (kind === "stock") {
        if (isEdit) {
          await updateStock.mutateAsync({
            ...updateCommon,
            reorderPoint: state.reorderPoint ? Number(state.reorderPoint) : null,
            leadTimeDays: state.leadTimeDays ? Number(state.leadTimeDays) : null,
            defaultSupplierId: state.defaultSupplierId || null,
          });
        } else {
          const r = await createStock.mutateAsync({
            ...createCommon,
            reorderPoint: state.reorderPoint ? Number(state.reorderPoint) : undefined,
            leadTimeDays: state.leadTimeDays ? Number(state.leadTimeDays) : undefined,
            defaultSupplierId: state.defaultSupplierId || undefined,
          });
          createdId = r.id;
        }
      } else if (kind === "semi") {
        const pe = { qcRequired: state.qcRequired, consumptionMethod: state.consumptionMethod, recipeId: state.recipeId || null };
        if (isEdit) await updateSemi.mutateAsync({ ...updateCommon, ...pe, reorderPoint: state.reorderPoint ? Number(state.reorderPoint) : null });
        else { const r = await createSemi.mutateAsync({ ...createCommon, ...pe, reorderPoint: state.reorderPoint ? Number(state.reorderPoint) : undefined }); createdId = r.id; }
      } else {
        const pe = { qcRequired: state.qcRequired, consumptionMethod: state.consumptionMethod, recipeId: state.recipeId || null };
        if (isEdit) await updateFinished.mutateAsync({ ...updateCommon, ...pe, reorderPoint: state.reorderPoint ? Number(state.reorderPoint) : null });
        else { const r = await createFinished.mutateAsync({ ...createCommon, ...pe, reorderPoint: state.reorderPoint ? Number(state.reorderPoint) : undefined }); createdId = r.id; }
      }
      await qc.invalidateQueries();
      if (createdId && onCreated) onCreated({ id: createdId, kind });
      onClose();
    } catch { /* errorMessage */ }
  }

  const titles = KIND_TITLE[kind];
  const uoms = uomQ.data ?? [];
  const allergenOpts = allergensQ.data ?? [];
  const types = typesQ.data ?? [];
  const customDefs = customDefsQ.data ?? [];
  const recipes = recipesQ.data?.rows ?? [];
  // Aranabilir reçete bağlama seçici (ad = etiket, kod = mono tail, durum = alt açıklama).
  const recipeOptions = useMemo<SSOption[]>(
    () => recipes.map((r) => ({
      value: r.id,
      label: `${r.producedName} · v${r.version}`,
      tail: r.producedCode,
      desc: RECIPE_STATUS[r.status] ?? r.status,
    })),
    [recipes],
  );

  // TEK DEĞİŞEN ŞEY SARMALAYICI. PageFormShell, Drawer ile aynı prop arayüzünü taklit
  // eder; böylece 500+ satırlık form gövdesi iki modda da tek koddan gelir.
  const Shell = variant === "page" ? PageFormShell : Drawer;
  const isPage = variant === "page";

  /* Belge künyesi için oluşturan + aktif tesis. Yalnız SAYFA modunda çekilir —
     çekmecede künye yok, boşuna istek atılmaz. */
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000, enabled: open && isPage });
  const me = meQ.data?.user ?? null;
  const olusturan = me ? `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim() : "";
  const aktifTesis = me
    ? (me.facilities.find((f) => f.id === (me.activeFacilityId ?? me.tesisId))?.name ?? null)
    : null;

  /**
   * FORM ID BENZERSİZ OLMALI — sabit "item-form" CANLI BİR HATAYDI.
   *
   * "Oluştur" düğmesi formun DIŞINDA (alt şeritte) durur ve forma `form="..."`
   * özniteliğiyle bağlanır. Sekmeler keep-alive olduğu için iki "Yeni …" sekmesi
   * (ya da bir sekme + bir düzenleme çekmecesi) AYNI ANDA mount kalıyor; sabit id
   * ile belgede iki `#item-form` oluşuyor ve tarayıcı düğmeyi BELGEDEKİ İLK forma
   * bağlıyordu. Sonuç: kullanıcı görünen sekmede "Oluştur"a basıyor, GİZLİ sekmenin
   * formu submit oluyor, "Ad zorunlu." hatası görünmeyen panelde basılıyor ve ekranda
   * HİÇBİR ŞEY OLMUYORDU (ağda istek bile yok). Ölçülerek doğrulandı.
   */
  const formId = useId();
  /**
   * ALAN ID'LERİ DE BENZERSİZ OLMALI — aynı gerekçe.
   * Sabit `if-name` / `if-uom` … ile iki "Yeni …" sekmesi açıkken belgede iki
   * aynı id oluşuyordu; `<label htmlFor="if-name">` belgedeki İLK eşleşmeye,
   * yani GİZLİ sekmenin girdisine bağlanıyordu. Sonuç: görünen sekmede etikete
   * tıklamak HİÇBİR ŞEYİ odaklamıyordu (gizli girdi odaklanamaz) ve ekran
   * okuyucu iki alanı ayırt edemiyordu. Ölçülerek doğrulandı.
   */
  const fid = (k: string) => `${formId}-${k}`;

  /**
   * Paketleme kartı KONTROLLÜ katlanır ve VARSAYILAN AÇIK.
   *
   * Açık başlaması ölçüme dayanıyor: 1352x918'de açıkken bile sağ kolon 529px
   * kalıyor (bütçe 680px), yani kartı kapalı başlatmanın dikey kazancı YOK —
   * karşılığında alanlar gizlenmiş olurdu. Katlanabilirlik yine de duruyor:
   * paketleme kullanmayan tesis kartı kapatıp gürültüyü azaltabilir.
   *
   * Efekt "yalnız aç, asla zorla kapatma": düzenlemede detay sorgusu geç
   * geldiğinde dolu paketleme verisi gizli kalmasın; kullanıcı eliyle
   * kapattıysa da (veri durumu değişmedikçe) kendiliğinden açılmasın.
   */
  const packFilled = [state.altUomId, state.altUomFactor, state.unitsPerPack, state.packsPerBox, state.boxesPerPallet]
    .filter((v) => String(v ?? "").trim() !== "").length;
  const packHasData = packFilled > 0;
  const [packOpen, setPackOpen] = useState(true);
  useEffect(() => { if (packHasData) setPackOpen(true); }, [packHasData]);

  // Çevrim faktörü ipucu DİNAMİK: temel birim başka kartta dursa bile kullanıcı
  // neyi neye çevirdiğini görür (yanlış çevrim → üretim raporunda yanlış kg).
  // Kartta kayıtlı ama Ayarlar listesinde artık bulunmayan alerjen adları.
  const oksuzAlerjen = useMemo(() => {
    const bilinen = new Set(allergenOpts.map((a) => a.name));
    return state.allergens.filter((n) => !bilinen.has(n));
  }, [state.allergens, allergenOpts]);

  const baseSym = uoms.find((u) => u.id === state.baseUomId)?.symbol ?? "temel birim";
  const altSym = uoms.find((u) => u.id === state.altUomId)?.symbol ?? "2. birim";

  return (
    <>
    <Shell
      open={open}
      onClose={onClose}
      size="lg"
      /* ESC / arka plan tıklaması formu ONAYSIZ SİLMESİN — depodaki 14 veri girişli
         çekmece de bu yüzden dismissible={false} geçiyor (Drawer.tsx:127 yorumu).
         Kapatma yolu duruyor: başlıktaki X ve alt şeritteki "İptal".
         PageFormShell bu prop'u kabul edip yok sayar, sayfa modu etkilenmez. */
      dismissible={false}
      title={isEdit ? titles.edit : titles.create}
      subtitle={isEdit ? `Kod: ${initial!.code}` : "Tüm zorunlu alanları doldurun (*)"}
      footer={
        <>
          {isEdit && initial ? (
            <button
              type="button"
              className="btn-md ghost"
              style={{ marginRight: "auto", color: initial.isActive ? "var(--danger)" : "var(--success)" }}
              onClick={() => setConfirmOpen(true)}
              disabled={isPending || toggling}
            >
              <Icon name={initial.isActive ? "archive" : "checkCircle"} size={15} />
              {toggling ? "İşleniyor…" : initial.isActive ? "Pasifleştir" : "Aktifleştir"}
            </button>
          ) : null}
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={isPending}>İptal</button>
          <button type="submit" form={formId} className="btn-md primary" disabled={isPending || !detailReady}>
            {isPending ? "Kaydediliyor…" : !detailReady ? "Yükleniyor…" : isEdit ? "Güncelle" : "Oluştur"}
          </button>
        </>
      }
    >
      {/* ═══════════════ FORM GÖVDESİ — İKİ KOLON + ÖNCELİK ═══════════════
          Sol kolon = her zaman doldurulan çekirdek · Sağ kolon = opsiyonel.
          Kırılım EKRANI değil bu kutuyu ölçer (.itf-form container) → aynı
          gövde geniş sekmede iki, dar çekmecede tek kolon olur. */}
      <form id={formId} className="itf-form" onSubmit={handleSubmit} noValidate>
        {errorMessage ? (
          <div className="form-banner-err" role="alert">
            <Icon name="alertTriangle" size={14} /><div>{errorMessage}</div>
          </div>
        ) : null}

        {/* ══ BELGE KÜNYESİ — yalnız SAYFA yerleşiminde ══
            Çekmecenin kendi başlık şeridi zaten künye görevi görüyor. `previewCode` isim +
            tür girilince CANLI çözülüyor; kod alanı ve rozet buraya taşındı. */}
        {/* ÇEKMECEDE DE BASILIR — yalnız künye satırı (oluşturan/tesis) sayfaya özel.
            Kod rozeti eskiden "Kimlik" kartının başlığındaydı; o kart alan şeridine
            dönüşünce rozet KAYBOLUYORDU. `previewCode` isim+tür girilince canlı çözülüyor;
            kullanıcı kaydetmeden önce kodunu görebilmeli. */}
        <DocHeader
          code={previewCode || null}
          pending={kind === "finished" ? "Kod, ad girilince oluşur (MM-…)" : "Kod, ad ve tür girilince oluşur"}
          kind={<>{KIND_LABEL[kind]}{isEdit ? "" : " · Yeni kayıt"}</>}
          meta={isPage
            ? [olusturan ? `Oluşturan ${olusturan}` : null, new Date().toLocaleDateString("tr-TR"), aktifTesis]
            : []}
          right={isEdit && initial ? (
            <span className={`badge no-dot ${initial.isActive ? "b-success" : ""}`}>
              {initial.isActive ? "Aktif" : "Pasif"}
            </span>
          ) : undefined}
        />

        {/* ══ ÇEKİRDEK ALAN ŞERİDİ — her kartta doldurulan alanlar, KUTUSUZ ══ */}
        <div className="itf-fields">
          {/* show=false ise bileşen null döner → ızgarada delik bırakmaz. */}
          <TesisPickerField show={tp.show} facilities={tp.facilities} value={tesisPick} onChange={setTesisPick} />

          <div className="form-field">
            <label className="lbl" htmlFor={fid("name")}>Ad <span className="req">*</span></label>
            <input id={fid("name")} className="form-input" type="text" value={state.name} onChange={(e) => setField("name", e.target.value)} maxLength={200} required />
          </div>

          <div className="form-field">
            <label className="lbl" htmlFor={fid("uom")}>Birim <span className="req">*</span></label>
            <select id={fid("uom")} className="form-select" value={state.baseUomId} onChange={(e) => setField("baseUomId", e.target.value)} required>
              <option value="">— Seçin —</option>
              {uoms.map((u) => <option key={u.id} value={u.id}>{u.symbol} — {u.name} ({UOM_DIM_LABEL[u.dimension] ?? u.dimension})</option>)}
            </select>
            <span className="help">Stok, lot ve reçete miktarlarının ölçüsü.</span>
          </div>

          {/* KOD ÖNEKİ KURALI GÖRÜNÜR (kural sunucuda: item-code.ts kodOneki — burada yalnız açıklanır):
              mamulde tür yalnız sınıflamadır, kod her zaman MM- (müşteri kararı 13.10.2026); ham madde /
              yarı mamulde türün kodu önek olur → seçenekte kod da yazılır ("AMB — Ambalaj").
              DÜZENLEMEDE kod yeniden üretilmez (update kodu ellemez; eski TRT-/NDL- mamuller öyle kalır) →
              metin ekrandaki koda ters düşmesin diye "kod değişmez" der, önek kuralını değil. */}
          <div className="form-field">
            <label className="lbl" htmlFor={fid("type")}>Tür / Sınıf</label>
            <select id={fid("type")} className="form-select" value={state.itemTypeId} onChange={(e) => setField("itemTypeId", e.target.value)} aria-describedby={fid("type-help")}>
              <option value="">— Tür yok —</option>
              {types.map((t) => <option key={t.id} value={t.id}>{kind === "finished" ? t.name : `${t.code} — ${t.name}`}</option>)}
            </select>
            <span className="help" id={fid("type-help")}>
              {isEdit
                ? (kind === "finished"
                  ? "Tür sınıflama içindir; kart kodu değişmez."
                  : "Tür değişse de kart kodu değişmez; tür kodu yalnız yeni kartlarda önek olur.")
                : (kind === "finished"
                  ? "Tür sınıflama içindir; mamul kodu her zaman MM- ile başlar."
                  : "Seçilen türün kodu kart kodunun öneki olur (ör. AMB-…).")}
            </span>
          </div>

          <div className="form-field itf-wide">
            <label className="lbl" htmlFor={fid("desc")}>Açıklama</label>
            <textarea id={fid("desc")} className="form-textarea itf-desc" value={state.description} onChange={(e) => setField("description", e.target.value)} maxLength={1000} rows={2} />
          </div>
        </div>

        {/* DÜZENLEMEDE VERİ RİSKİ UYARISI — eskiden "Kimlik" kartının dip şeridindeydi;
            kart alan şeridine dönüşünce KAYBOLMUŞTU. Birim değişikliği geçmiş lotları
            çevirmediği için bu bir uyarı, süsleme değil. */}
        {isEdit ? (
          <div className="form-banner-warn itf-edit-warn">
            <Icon name="alertTriangle" size={14} />
            <div>Kod değiştirilemez — adı düzeltmek kodu düzeltmez. <strong>Birim değişirse eski lotlar çevrilmez.</strong></div>
          </div>
        ) : null}

        {/* KART IZGARASI — kartlar DOĞRUDAN ızgara öğesidir.
            Eskiden iki `.itf-col` sarmalayıcısı vardı; auto-fit'e geçince ızgara kartları
            değil O İKİ SARMALAYICIYI yerleştiriyordu → geniş ekranda en fazla 2 sütun ve
            kolonlar arası 200px'lik yükseklik farkı (ölçüldü: 299 / 500). Sarmalayıcısız
            akışta her kart kendi hücresini alır, koşullu kartlar delik bırakmaz. */}
        <div className="itf-cols">
            {/* ── TERNARY YUVA — kind'a göre yer değiştirir, iki dalda da DOLU ── */}
            {kind !== "stock" ? (
              <Grp
                icon="factory"
                title="Üretim ve Planlama"
                hint="reçete · QC · eşik"
                /* Not TEK SATIRDA kalmalı (~95 karakter): 571px'lik kartta ikinci
                   satır sol kolona 17px ekler ve kaydırmasızlık payını yer. */
                /* KAPSAM DOĞRU YAZILDI: qc_required yalnız ÜRETİM yollarında okunuyor
                   (production-batch.ts / production.ts). Mal kabul ve toplu içe aktarım
                   lotu her hâlükârda "released" doğuruyor — "her lot" demek yanlış
                   güven verirdi. Backend o boşluğu kapatırsa bu not güncellenmeli. */
                note={<>Açıksa <strong>üretilen</strong> her lot “QC bekliyor” doğar; serbest bırakılmadan tüketilemez, sevk edilemez.</>}
              >
                <div className="itf-fg">
                  <div className="form-field itf-wide">
                    <label className="lbl" htmlFor={fid("recipe")}>Reçete <span className="itf-lbl-aux">— hazır reçeteden seç</span></label>
                    <SearchSelect
                      id={fid("recipe")}
                      value={state.recipeId || null}
                      options={recipeOptions}
                      onChange={(v) => setField("recipeId", v ?? "")}
                      placeholder="— Reçete bağlama —"
                      searchPlaceholder="Reçete ürün adı / kodu ara…"
                      emptyText="Reçete bulunamadı"
                    />
                    <span className="help">Hazır bir reçeteyi bu ürüne bağla (ortak kütüphane).</span>
                  </div>

                  <div className="form-field">
                    <span className="lbl">Kalite Kontrol</span>
                    <label className="form-checkbox" htmlFor={fid("qc")}>
                      <input id={fid("qc")} type="checkbox" checked={state.qcRequired} onChange={(e) => setField("qcRequired", e.target.checked)} />
                      <span>QC onayı gerektir</span>
                    </label>
                  </div>

                  <div className="form-field">
                    <span className="lbl" id={fid("cm-lbl")}>Tüketim Yöntemi</span>
                    <div className="form-radio-group" role="radiogroup" aria-labelledby={fid("cm-lbl")}>
                      <label className="form-radio"><input type="radio" name="consumptionMethod" checked={state.consumptionMethod === "backflush"} onChange={() => setField("consumptionMethod", "backflush")} /><span>Backflush</span></label>
                      <label className="form-radio"><input type="radio" name="consumptionMethod" checked={state.consumptionMethod === "manual"} onChange={() => setField("consumptionMethod", "manual")} /><span>Manuel</span></label>
                    </div>
                    {/* Alan bugün YALNIZ kaydediliyor; hiçbir üretim/tüketim motoru
                        okumuyor (yalnız Excel içe aktarımı yazıyor). Motor okumaya
                        başlarsa bu yardım metni GÜNCELLENMELİ. */}
                    <span className="help">Şu an bilgi amaçlı — bileşenler iki seçenekte de FEFO ile düşer.</span>
                  </div>

                  {/* Kritik eşik burada: mamulde sipariş noktası bir ÜRETİM tetikleyicisi. */}
                  <div className="form-field">
                    <label className="lbl" htmlFor={fid("rop")}>Kritik Stok Eşiği</label>
                    <input id={fid("rop")} className="form-input itf-num" type="number" step="any" min={0} value={state.reorderPoint} onChange={(e) => setField("reorderPoint", e.target.value)} inputMode="decimal" />
                    {/* Yardım metni KISA: 182px'lik sütunda her satır ~25 karakter;
                        uzun metin tüm ızgara satırını yükseltiyordu. Ayrıntı kart
                        notunda. "0 kabul edilmez" backend kuralı (reorderPoint
                        zod'da positive()) — kullanıcı 0 yazıp kaydedemiyordu. */}
                    <span className="help">Boş = uyarı yok · 0 kabul edilmez.</span>
                  </div>
                </div>
              </Grp>
            ) : (
              <Grp
                icon="truck"
                title="Tedarik ve Planlama"
                hint="MRP girdisi"
                note={<>Tedarik süresi yalnız MRP önerisinde gösterilir; sistem <strong>sipariş tarihi hesaplamaz</strong>.</>}
              >
                {/* İKİ KOLON: sayısal ikili üstte yan yana, tedarikçi ALT SATIRDA tam
                    genişlik. Üçü tek satıra dizilince her alan ~150px'lik dar bir kutuya
                    sıkışıyor, tedarikçi adı da o kutuda kırpılıyordu. */}
                <div className="itf-fg two">
                  <div className="form-field">
                    <label className="lbl" htmlFor={fid("rop")}>Kritik Stok Eşiği</label>
                    <input id={fid("rop")} aria-describedby={fid("rop-help")} className="form-input itf-num" type="number" step="any" min={0} value={state.reorderPoint} onChange={(e) => setField("reorderPoint", e.target.value)} inputMode="decimal" />
                    <span className="help" id={fid("rop-help")}>Sipariş noktası · MRP. 0 kabul edilmez.</span>
                  </div>
                  <div className="form-field">
                    <label className="lbl" htmlFor={fid("lead")}>Tedarik Süresi (gün)</label>
                    <input id={fid("lead")} className="form-input itf-num" type="number" min={0} value={state.leadTimeDays} onChange={(e) => setField("leadTimeDays", e.target.value)} inputMode="numeric" />
                  </div>
                  <div className="form-field itf-wide">
                    <label className="lbl" htmlFor={fid("defsup")}>Varsayılan Tedarikçi</label>
                    <SearchSelect
                      id={fid("defsup")}
                      value={state.defaultSupplierId || null}
                      options={supplierOptions}
                      onChange={(v) => setField("defaultSupplierId", v ?? "")}
                      placeholder="— Tedarikçi seç —"
                      searchPlaceholder="Tedarikçi ara…"
                    />
                    <span className="help">MRP önerileri ve satın alma talepleri bununla ön-dolar.</span>
                  </div>
                </div>
              </Grp>
            )}

            <Grp
              icon="flame"
              title="Gıda Güvenliği ve İzlenebilirlik"
              /* Rozet master listeyle KESİŞTİRİLİR: Ayarlar'da bir alerjen yeniden
                 adlandırılırsa kartta eski ad state'te kalıyor — sayıya giriyor ama
                 karşılık gelen pill YOK. "2 seçili" deyip 1 pill göstermek, hukuki
                 sonuç doğuran alerjen beyanında yanlış güven verirdi. */
              hint={allergenOpts.length ? `${state.allergens.length} seçili${oksuzAlerjen.length ? ` · ${oksuzAlerjen.length} tanımsız` : ""}` : undefined}
              hintTitle={oksuzAlerjen.length ? `Ayarlar listesinde olmayan: ${oksuzAlerjen.join(", ")}` : undefined}
              note={<>Raf ömrü <strong>SKT'yi ve FEFO sırasını</strong> üretir. Alerjen kartın beyanıdır, reçeteden miras alınmaz.</>}
            >
              {/* İKİ KOLON: Raf Ömrü | Alerjenler. Alerjenler eskiden tam satırdı; raf
                  ömrünün yanı boş kalıyor, kart gereksiz uzuyordu. İkisi de aynı beyanın
                  parçası — yan yana okunmaları da doğru. */}
              <div className="itf-fg two">
                <div className="form-field">
                  <label className="lbl" htmlFor={fid("shelf")}>Raf Ömrü (gün)</label>
                  <input id={fid("shelf")} className="form-input itf-num" type="number" min={1} value={state.shelfLifeDays} onChange={(e) => setField("shelfLifeDays", e.target.value)} inputMode="numeric" />
                  <span className="help">Boş = sınırsız (lot SKT'siz doğar).</span>
                </div>

                <div className="form-field">
                  <span className="lbl" id={fid("alg-lbl")}>Alerjenler</span>
                  {allergenOpts.length === 0 ? (
                    <span className="help">Ayarlar → Alerjenler'den liste oluşturun.</span>
                  ) : (
                    <div className="itf-alg" role="group" aria-labelledby={fid("alg-lbl")}>
                      {allergenOpts.map((a) => {
                        const on = state.allergens.includes(a.name);
                        return (
                          <button key={a.id} type="button" className="itf-alg-pill" aria-pressed={on} onClick={() => toggleAllergen(a.name)}>
                            {on ? <Icon name="check" size={12} /> : null}{a.name}
                          </button>
                        );
                      })}
                      {/* Öksüz beyanlar GÖRÜNÜR ve silinebilir olmalı: aksi halde
                          rozetteki sayı ile ekrandaki pill'ler tutmaz ve kullanıcı
                          ölü bir alerjen beyanını temizleyemez (kaydedince geri yazılır). */}
                      {oksuzAlerjen.map((n) => (
                        <button key={`oksuz-${n}`} type="button" className="itf-alg-pill oksuz" aria-pressed={true}
                          title="Ayarlar listesinde yok — kaldırmak için tıklayın"
                          onClick={() => toggleAllergen(n)}>
                          <Icon name="alertTriangle" size={12} />{n}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Grp>

            {/* Katlanır: ilk doldurmada en çok karıştırılan 5 alan ekrandan ve Tab
                zincirinden çıkar. `open` kontrollü → dolu veri asla gizlenmez. */}
            <Fold
              icon="boxes"
              title="Paketleme ve 2. Birim"
              hint={packFilled > 0 ? `${packFilled} alan dolu` : "isteğe bağlı"}
              open={packOpen}
              onToggle={setPackOpen}
              /* Not, üretim raporunun GERÇEK hesabına göre yazıldı (production-report.ts
                 metricsFor): temel miktar KOLİ; Paket = koli × Koli İçi; Adet = koli ×
                 Koli İçi × Paket İçi (İKİSİ de dolu olmalı); Palet = koli ÷ Palet İçi
                 (diğerlerinden bağımsız); kg = koli × Çevrim Faktörü. */
              note={<>Rapor miktarı <strong>koli</strong> cinsinden: Paket = koli×Koli İçi · Adet ayrıca <strong>Paket İçi</strong> ister · Palet = koli÷Palet İçi (bağımsız).</>}
            >
              <div className="itf-fg">
                <div className="form-field">
                  <label className="lbl" htmlFor={fid("altuom")}>2. Birim</label>
                  <select id={fid("altuom")} className="form-select" value={state.altUomId} onChange={(e) => setField("altUomId", e.target.value)}>
                    <option value="">— Yok —</option>
                    {uoms.map((u) => <option key={u.id} value={u.id}>{u.symbol} — {u.name}</option>)}
                  </select>
                  {/* Sembol hiçbir rapora BASILMIYOR (üretim raporu sütunu sabit "kg"
                      yazar); rapora giren tek şey Çevrim Faktörü'nün çarpımıdır. */}
                  <span className="help">Stok hep temel birimde işlenir; rapora yalnız çevrim değeri girer.</span>
                </div>
                <div className="form-field">
                  <label className="lbl" htmlFor={fid("altf")}>Çevrim Faktörü</label>
                  <input id={fid("altf")} className="form-input itf-num" type="number" step="any" min={0} value={state.altUomFactor} onChange={(e) => setField("altUomFactor", e.target.value)} inputMode="decimal" placeholder={`1 ${baseSym} = ? ${altSym}`} />
                </div>
              </div>

              {/* Sayısal zincir TEK SATIRDA — çarpım görsel olarak da zincir gibi okunsun. */}
              <div className="itf-fg num">
                <div className="form-field">
                  <label className="lbl" htmlFor={fid("pack")}>Paket İçi</label>
                  <input id={fid("pack")} className="form-input itf-num" type="number" step="any" min={0} value={state.unitsPerPack} onChange={(e) => setField("unitsPerPack", e.target.value)} inputMode="decimal" placeholder="adet/paket" />
                </div>
                <div className="form-field">
                  <label className="lbl" htmlFor={fid("box")}>Koli İçi</label>
                  <input id={fid("box")} className="form-input itf-num" type="number" step="any" min={0} value={state.packsPerBox} onChange={(e) => setField("packsPerBox", e.target.value)} inputMode="decimal" placeholder="paket/koli" />
                </div>
                <div className="form-field">
                  <label className="lbl" htmlFor={fid("pallet")}>Palet İçi</label>
                  <input id={fid("pallet")} className="form-input itf-num" type="number" step="any" min={0} value={state.boxesPerPallet} onChange={(e) => setField("boxesPerPallet", e.target.value)} inputMode="decimal" placeholder="koli/palet" />
                </div>
              </div>
            </Fold>

            {/* Özel alanlar KATLANMAZ: `required` bayrağı istemcide doğrulanmıyor,
                katlanırsa görmeden kaydedilir. 0 tanımlıysa kart hiç basılmaz. */}
            {customDefs.length > 0 ? (
              <Grp icon="layers" title="Özel Alanlar" hint={`${customDefs.length} alan`} note="Ayarlar → Özel Alanlar'dan yönetilir; kart türüne göre değişir.">
                <div className="itf-fg">
                  {customDefs.map((d) => {
                    const val = state.customFields[d.id] ?? "";
                    const cfId = fid(`cf-${d.id}`);
                    return (
                      <div className="form-field" key={d.id}>
                        <label className="lbl" htmlFor={cfId}>
                          {d.label}{d.required ? <span className="req">*</span> : null}
                          {d.unit ? <span className="itf-lbl-aux"> ({d.unit})</span> : null}
                        </label>
                        {d.dataType === "boolean" ? (
                          <label className="form-checkbox" htmlFor={cfId}><input id={cfId} type="checkbox" checked={val === "true"} onChange={(e) => setCustomField(d.id, e.target.checked ? "true" : "")} /><span>Evet</span></label>
                        ) : d.dataType === "select" ? (
                          <select id={cfId} className="form-select" value={val} onChange={(e) => setCustomField(d.id, e.target.value)}>
                            <option value="">— Seçin —</option>
                            {d.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input id={cfId} className={`form-input${d.dataType === "number" ? " itf-num" : ""}`} type={d.dataType === "number" ? "number" : "text"} step="any" value={val} onChange={(e) => setCustomField(d.id, e.target.value)} inputMode={d.dataType === "number" ? "decimal" : undefined} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Grp>
            ) : null}
        </div>

        {/* ══ KAYIT SONRASI NE OLACAK ══
            Maddeler sistemin GERÇEK davranışıdır; süsleme değildir. Yalnız SAYFA
            yerleşiminde: çekmecede dikey bütçe yok ve bu bilgi kart notlarında zaten var. */}
        {isPage && !isEdit ? (
          <section className="fgroup" style={{ marginTop: 12 }}>
            <div className="fgroup-head">
              <span className="ic"><Icon name="checkCircle" size={13} /></span>
              <span className="t">Kayıt Sonrası</span>
              <span className="s" title="Oluştur'a basınca ne olacak">ne olacak</span>
            </div>
            <div className="fgroup-body">
              <div className="itf-after-row">
                <span className="ic"><Icon name="check" size={14} /></span>
                <span className="tx">
                  Kart kodu üretilip <strong>sabitlenir</strong>{previewCode ? <> — <span className="mono">{previewCode}</span></> : null}
                </span>
                <span className="meta">adı değişse de kod değişmez</span>
              </div>
              <div className="itf-after-row">
                <span className="ic off"><Icon name="chevRight" size={14} /></span>
                <span className="tx">
                  {kind === "stock"
                    ? <>Mal kabulde ve <strong>reçete bileşeni</strong> olarak seçilebilir</>
                    : <>Reçetelerde ve <strong>üretim çıktısı</strong> olarak seçilebilir</>}
                </span>
                <span className="meta">{KIND_LABEL[kind]}</span>
              </div>
              <div className="itf-after-row">
                <span className="ic off"><Icon name="chevRight" size={14} /></span>
                <span className="tx">
                  {state.shelfLifeDays
                    ? <>Lotlar <strong>{state.shelfLifeDays} gün</strong> raf ömrüyle doğar; FEFO sırası SKT'den çözülür</>
                    : <>Raf ömrü girilmedi — lotlar <strong>SKT'siz</strong> doğar, FEFO sırası oluşmaz</>}
                </span>
                <span className="meta">{state.shelfLifeDays ? "SKT'li" : "SKT'siz"}</span>
              </div>
              {kind === "stock" ? (
                <div className="itf-after-row">
                  <span className="ic off"><Icon name="chevRight" size={14} /></span>
                  <span className="tx">
                    {state.reorderPoint
                      ? <>Stok <strong>{state.reorderPoint}</strong> altına düşünce MRP satın alma önerir</>
                      : <>Kritik eşik girilmedi — MRP bu kart için <strong>öneri üretmez</strong></>}
                  </span>
                  <span className="meta">MRP</span>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
      </form>
    </Shell>

    {isEdit && initial ? (
      <ConfirmDialog
        open={confirmOpen}
        tone={initial.isActive ? "danger" : "primary"}
        title={initial.isActive ? "Kaydı pasifleştir?" : "Kaydı aktifleştir?"}
        message={initial.isActive
          ? <><b>{initial.code}</b> — {initial.name} kaydı <b>pasifleştirilecek</b> (soft delete). Listede gizlenir; istediğinizde geri alabilirsiniz.</>
          : <><b>{initial.code}</b> — {initial.name} kaydı tekrar <b>aktifleştirilecek</b>.</>}
        confirmLabel={initial.isActive ? "Pasifleştir" : "Aktifleştir"}
        cancelLabel="Vazgeç"
        busy={toggling}
        onConfirm={doToggleActive}
        onCancel={() => setConfirmOpen(false)}
      />
    ) : null}
    </>
  );
}
